import type { FindingRecord, Severity } from './types';

/**
 * Phase 4 attack-path visualizer — derives a small graph from EXISTING findings, rather than
 * running real graph analysis (BloodHound-style "shortest path to Domain Admin" traversal was
 * explicitly scoped out of packages/kali-parsers/src/bloodhound.ts as needing a dedicated,
 * carefully-verified analysis engine, not a per-tool parser or a frontend page). This module is
 * that same honest scoping applied here: for each attack-relevant finding, it draws ONE edge from
 * the affected asset to a fixed, technique-category "goal" node — a lightweight, defensible
 * visualization of "these techniques were found, pointing toward this kind of impact," not a
 * claim of a computed, verified path through the environment.
 */

export type AttackNodeKind = 'account' | 'host' | 'goal';

export interface AttackNode {
  id: string;
  label: string;
  kind: AttackNodeKind;
}

export interface AttackEdge {
  id: string;
  source: string;
  target: string;
  technique: string;
  severity: Severity;
  findingId: string;
}

export interface AttackGraph {
  nodes: AttackNode[];
  edges: AttackEdge[];
}

const GOAL_DOMAIN_ADMIN = { id: 'goal:domain-admin', label: 'Domain Admin' };
const GOAL_LATERAL_MOVEMENT = { id: 'goal:lateral-movement', label: 'Lateral Movement' };
const GOAL_LOCAL_ADMIN = { id: 'goal:local-admin', label: 'Local Admin / Credential Theft' };

/**
 * Maps a finding's `moduleId` to the attack-goal category it points toward. Only the module ids
 * this platform's AD/red-team tooling actually produces are covered — everything else (web-app
 * findings, TLS hygiene, etc.) has no attack-path semantics and is excluded (see `isPathRelevant`).
 */
function goalForModuleId(moduleId: string): { id: string; label: string } | undefined {
  if (moduleId === 'kali.bloodhound' || moduleId === 'kali.ldapdomaindump' || moduleId === 'kali.kerbrute') {
    return GOAL_DOMAIN_ADMIN;
  }
  if (moduleId === 'kali.enum4linux') {
    return GOAL_LATERAL_MOVEMENT;
  }
  if (moduleId.startsWith('endpoint.windows.')) {
    return GOAL_LOCAL_ADMIN;
  }
  return undefined;
}

export function isPathRelevant(finding: FindingRecord): boolean {
  return goalForModuleId(finding.moduleId) !== undefined;
}

/** A finding's `target` value is either an account-shaped string (contains "@", e.g.
 * `jdoe@corp.local`) or a host/share-shaped string — used only to color/label the source node,
 * never to change which goal it points toward. */
function nodeKindForTarget(target: string): AttackNodeKind {
  return target.includes('@') ? 'account' : 'host';
}

export function buildAttackGraph(findings: FindingRecord[]): AttackGraph {
  const nodes = new Map<string, AttackNode>();
  const edges: AttackEdge[] = [];

  for (const finding of findings) {
    const goal = goalForModuleId(finding.moduleId);
    if (!goal) continue;

    const sourceId = `asset:${finding.target}`;
    if (!nodes.has(sourceId)) {
      nodes.set(sourceId, { id: sourceId, label: finding.target, kind: nodeKindForTarget(finding.target) });
    }
    if (!nodes.has(goal.id)) {
      nodes.set(goal.id, { id: goal.id, label: goal.label, kind: 'goal' });
    }

    edges.push({
      id: finding.id,
      source: sourceId,
      target: goal.id,
      technique: finding.title,
      severity: finding.severity,
      findingId: finding.id,
    });
  }

  return { nodes: [...nodes.values()], edges };
}
